import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import JSZip from "jszip";
import Icon1 from "../icons/Icon.js";
import Spinner from "../loading/Spinner.js";
import SmallSpinner from "../loading/SmallSpinner.js";

const Dropzone2 = ({
  onDrop,
  files = [],
  maxFiles = Infinity,
  maxSize = 52428800,
  dropzoneStyles = "",
  instructionsText = "Glissez vos fichiers ici, ou cliquez pour sélectionner des fichiers",
  onDelete,
  loading,
  mandatory = false,
  only_zip = false,
  handleClickVariant,
}) => {
  const [loadingData, setLoadingData] = React.useState(false);

  const onDropAccepted = useCallback(
    async (acceptedFiles) => {
      setLoadingData(true);

      let newFiles = [];
      for (const file of acceptedFiles) {
        if (
          ![
            "text/xml",
            "application/x-zip-compressed",
            "application/zip",
            "application/xml",
          ].includes(file.type)
        ) {
          if (only_zip) {
            console.error(file.type);
            handleClickVariant(
              "Veuillez déposer un fichier au bon format",
              "error"
            );
            newFiles = [];
            break;
          } else {
            newFiles.push(file); // Add non-ZIP files directly
          }
        } else {
          newFiles.push(file); // Add non-ZIP files directly
        }
      }

      if (newFiles.length > 0) {
        onDrop([...files, ...newFiles]); // Append new files to existing ones
      }

      setLoadingData(false);
    },
    [onDrop, files]
  );

  const onDropRejected = useCallback(
    (rejectedFiles) => {
      rejectedFiles.forEach((file) => {
        if (file.file.size > maxSize) {
          console.error(
            `Rejected file due to size: ${file.file.name}. Size: ${file.file.size} bytes. Max allowed size: ${maxSize} bytes.`
          );
        } else {
          console.warn(
            `Rejected file: ${file.file.name}, Type: ${file.file.type}`
          );
        }
      });
      const fileNames = rejectedFiles.map((file) => file.file.name);
      alert(`Invalid file types or sizes: ${fileNames.join(", ")}`);
    },
    [maxSize]
  );

  const [onHover, setOnHover] = React.useState(false);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropAccepted,
    onDropRejected,
    accept: {
      "application/pdf": [],
      "application/xml": [],
      "application/zip": [],
      "application/x-zip-compressed": [],
      "text/xml": [],
    },
    maxFiles,
    maxSize,
    multiple: true,
  });

  return (
    <div className="flex flex-col items-center w-full  py-2  gap-4">
      <div
        {...getRootProps()}
        onMouseEnter={() => setOnHover(true)}
        onMouseLeave={() => setOnHover(false)}
        className={`border-3 border-dashed h-c100 rounded-2xl p-4 w-full flex items-center justify-center text-center cursor-pointer ${
          mandatory
            ? "border-red-500 bg-red-100"
            : isDragActive
            ? "border-blue-500 bg-blue-50"
            : onHover
            ? "bg-blue-50 border-shadow_25"
            : "border-primary bg-transparent"
        } ${dropzoneStyles}`}
      >
        <input {...getInputProps()} />
        {loadingData ? (
          <Spinner loading={true} />
        ) : (
          <div className="flex flex-col items-center w-full">
            {isDragActive ? (
              <div className="flex flex-col items-center text-h3 gap-2 text-shadow_50">
                <Icon1
                  icon_name="join"
                  size={30}
                  fill="rgba(80, 81, 255, 0.7)"
                />
                <div className="text-h1 font-bold text-black w-c200">
                  Lâchez pour déposer
                </div>
              </div>
            ) : (
              (!loading && (
                <div className="flex flex-col items-center text-h3 gap-2 text-shadow_50">
                  <Icon1
                    icon_name="file"
                    size={30}
                    fill="rgba(80, 81, 255, 0.7)"
                  />
                  <div className="text-h1 font-bold text-black w-c200">
                    Déposez vos fichiers ici
                  </div>
                </div>
              )) ||
              (loading && (
                <div className="flex flex-col items-center text-h3 gap-2 text-shadow_50">
                  <Icon1
                    icon_name="file"
                    size={30}
                    fill="rgba(80, 81, 255, 0.7)"
                  />
                  <div className="text-h1 font-bold text-black w-c200">
                    Téléchargement en cours ...
                  </div>
                  <SmallSpinner loading={true} margin={0} size={30} />
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Display the dropped files */}
      <div className="w-full items-start justify-start rounded-lg flex flex-col min-h-fit h-fit py-2 gap-2  max-h-c300 ">
        {files.length !== 0 ? (
          files.map((file, index) => (
            <div
              key={index}
              className="w-full flex-row flex justify-between items-center"
            >
              <div className="flex flex-row gap-3 items-center">
                <Icon1 icon_name="file" size={25} fill="#504df9" />
                <div className="flex flex-col items-start justify-start overflow-y-auto">
                  <div className="text-h3 text-black">
                    {file.name?.length > 20
                      ? file.name.substring(0, 20) + "..."
                      : file.name}
                  </div>
                  <div className="text-h4 text-shadow_50">
                    {file.name.endsWith(".zip") ? "Fichier ZIP" : "Fichier PDF"}
                    {file.size && ` - ${(file.size / 1000).toFixed(1)} Ko`}
                  </div>
                </div>
              </div>
              <div className="cursor-pointer" onClick={() => onDelete(file)}>
                <Icon1 icon_name="close" size={20} fill="red" />
              </div>
            </div>
          ))
        ) : (
          <div className="text-center text-h2 text-shadow_50 h-full w-full min-h-10 justify-center flex flex-col">
            Pas de document déposé
          </div>
        )}
      </div>
    </div>
  );
};

export default Dropzone2;

export const processZipFile = async (zipFile) => {
  const zip = new JSZip();
  const content = await zip.loadAsync(zipFile);
  const xmlFile = Object.keys(content.files).find((fileName) =>
    fileName.endsWith(".xml")
  );

  if (xmlFile) {
    const xmlContent = await zip.file(xmlFile).async("string");
    const blob = new Blob([xmlContent], { type: "application/xml" });
    const extractedFile = new File([blob], xmlFile, {
      type: "application/xml",
    });
    return extractedFile;
  }

  return null; // Return null if no XML file is found
};
